import React, { useCallback, useEffect, useReducer, useState } from "react";

import { faClose, faFilter, faRefresh, faShareNodes } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { EnvelopeFill, Facebook, Linkedin, Twitter } from "react-bootstrap-icons";
import Drawer from "react-modern-drawer";
import { toast } from "react-toastify";

import { ETHNIC_TEXT } from "@/constants";
import { MainPageTabs } from "@/enum/common";
import TextChart from "@/features/explore/charts/TextChart";
import ExploreTab from "@/features/explore/ExploreTab";
import MainFilters from "@/features/explore/filters/MainFilters";
import useExploreMobileView from "@/features/explore/hooks/useExploreMobileView";
import { handleAxiosError, handleAxiosSuccess } from "@/helpers/common";
import { getInitialMainFilter, transformToOurFilter } from "@/helpers/filters";
import { ROUTES } from "@/helpers/routes";
import useCheckMobileScreen from "@/hooks/useCheckMobileScreen";
import { useFilters } from "@/providers/FiltersProvider";
import { filtersReducer, initialFilterState, resetFilterDataAction, setFilterLoaderAction, setOriginalFilterDataAction } from "@/reducers/filterReducer";
import { saveFilterSearchService } from "@/services/filterServices";
import CustomModal from "@/shared/components/CustomModal";
import Layout from "@/shared/components/layout";
import Loader from "@/shared/components/Loader";
import PageSeo from "@/shared/components/PageSeo";
import TextChartLoader from "@/shared/components/TextChartLoader";
import { ITabsData } from "@/types/Filters";

function Explore() {
    const isMobile = useCheckMobileScreen();
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [tabsListData, setTabsListData] = useState<ITabsData[]>([]);
    const { mainFilters, setMainFilters, setTempFilters, setSelectedFilterItem, showDrawer, setShowDrawer, redirectFilters, setRedirectFilters, selectedTab, setSelectedTab } = useFilters();
    const [searchFilterData, dispatch] = useReducer(filtersReducer, initialFilterState);
    const { isLoading, data: searchData } = searchFilterData;

    console.log("searchFilterData:-", searchFilterData, mainFilters, redirectFilters);

    useEffect(() => {
        saveFilterSearchService(getInitialMainFilter())
            .then(res => {
                if (res.data.data) {
                    const { filterData } = res.data.data;
                    dispatch(setOriginalFilterDataAction(res.data.data));
                    if (filterData) {
                        const t = transformToOurFilter(filterData);
                        // console.log("filterData:-", filterData, t);
                        if (redirectFilters) {
                            setTempFilters(redirectFilters);
                            setShowDrawer(true);
                        } else {
                            setTempFilters(t);
                        }
                        setMainFilters(t);
                    }
                    dispatch(setFilterLoaderAction(false));
                } else {
                    toast.error(res.data.message, { icon: false });
                }
            }).catch(err => {
                dispatch(setFilterLoaderAction(false));
                handleAxiosError(err, toast);

            });

        return () => {
            dispatch(resetFilterDataAction());
            setMainFilters(null);
            setTempFilters(null);
            setRedirectFilters(null);
            setSelectedTab(MainPageTabs.regions);
            setShowDrawer(false);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useExploreMobileView({ setTabsListData });

    const toggleModal = () => {
        setIsModalOpen((prevState) => !prevState);
    };

    const toggleDrawer = useCallback(() => {
        setShowDrawer((prevState: boolean) => !prevState);
        gtag("event", "Filters", { description: "Opened" });
    }, [setShowDrawer]);

    const handleDrawerOverlayClick = useCallback(() => {
        setSelectedFilterItem(null);
        setTempFilters(mainFilters);
        setShowDrawer(false);
    }, [mainFilters, setSelectedFilterItem, setShowDrawer, setTempFilters]);

    const handleSelectTab = useCallback((value: MainPageTabs) => {
        setSelectedTab(value);
        gtag("event", "Tab View", { description: value });
    }, [setSelectedTab]);

    const getActiveFiltersCount = useCallback(() => {
        if (mainFilters) {
            return Object.values(mainFilters).length;
        }
    }, [mainFilters]);

    const isThisSearchOld = useCallback(() => {
        if (searchData?.calledAt) {
            const d = new Date(searchData?.calledAt);
            const today = new Date();
            today.setHours(0, 0, 0);
            return d.getTime() < today.getTime();
        }
        return false;
    }, [searchData?.calledAt]);

    const getCreatedDate = () => {
        if (searchData?.calledAt) {
            const d = new Date(searchData.calledAt);

            const ye = new Intl.DateTimeFormat("en", { year: "numeric" }).format(d);
            const mo = new Intl.DateTimeFormat("en", { month: "2-digit" }).format(d);
            const da = new Intl.DateTimeFormat("en", { day: "2-digit" }).format(d);
            return `${da}/${mo}/${ye}`;
        }
    };

    const handleRefresh = useCallback(async () => {
        try {
            dispatch(setFilterLoaderAction(true));
            const res = await saveFilterSearchService(mainFilters, true);
            dispatch(setFilterLoaderAction(false));
            if (res.data.data) {
                handleAxiosSuccess("Results refreshed", toast);
                const { filterData } = res.data.data;
                dispatch(setOriginalFilterDataAction(res.data.data));
                if (filterData) {
                    const t = transformToOurFilter(filterData);
                    // console.log("filterData:-", filterData, t);
                    setTempFilters(t);
                    setMainFilters(t);
                }
            } else {
                toast.error(res.data.message, { icon: false });
            }
        } catch (error) {
            handleAxiosError(error, toast);
        }
    }, [mainFilters, setTempFilters, setMainFilters]);

    const getSearchUrl = useCallback(() => {
        if (typeof window !== "undefined") {
            return location.href;
        }
        return "";
    }, []);

    const handleCopy = useCallback(async () => {
        await navigator.clipboard.writeText(getSearchUrl());
        toast("Link copied", {
            type: "success",
        });
        gtag("event", "Share Report", { description: "Copy" });

    }, [getSearchUrl]);

    const setGAnalytics = (category: string) => {
        gtag("event", "Share Report", { description: category });
    };

    const getGender = useCallback(() => {
        return mainFilters?.["D001"]?.data[0]?.label.toLowerCase();
    }, [mainFilters]);

    const getMainTitle = () => {
        if (!getGender()) {
            return null;
        } else if (searchFilterData?.regions?.data && searchFilterData?.regions?.data.length > 0) {
            return `UK ${getGender()}-led companies ${new Date().getFullYear()}`;
        } else {
            return null;
        }
    };

    return (
        <Layout isExplorePage={true} hideFooter>
            <PageSeo title="Explore | The Gender Index" pathname={ROUTES.explore} />
            <div className='singleLayout fullWidth theGenderIndex'>
                <div className='flexSpaceBtw headWrap'>
                    <div className="searchTitleCont">
                        <h2>{getMainTitle()}</h2>
                        {
                            (searchFilterData?.regions?.data && searchFilterData?.regions?.data.length > 0) && isMobile
                            && <button
                                className="unStyledButton"
                                onClick={toggleModal}>
                                <FontAwesomeIcon icon={faShareNodes} className="mr8" />{!isMobile ? "Share report" : ""}</button>
                        }
                    </div>
                    <div>

                        {
                            isThisSearchOld()
                            && <div className="date-accurate">
                                Data accurate as of <span>{getCreatedDate()}</span>
                            </div>
                        }
                    </div>
                    <div className="headerRightActions">
                        {
                            isThisSearchOld()
                            && <button
                                className="unStyledButton mr40"
                                onClick={handleRefresh}
                                disabled={isLoading}>
                                <FontAwesomeIcon icon={faRefresh} className="mr8" />{!isMobile ? "Refresh latest data" : ""}
                            </button>
                        }
                        {
                            !isMobile
                            && ((searchFilterData?.regions?.data && searchFilterData?.regions?.data.length > 0) || isLoading)
                            && <button
                                className="unStyledButton mr40"
                                onClick={toggleModal}
                                disabled={isLoading}>
                                <FontAwesomeIcon
                                    icon={faShareNodes}
                                    className="mr8" />
                                {"Share report"}
                            </button>
                        }

                        <button
                            onClick={toggleDrawer}
                            className="btn-blue"
                            disabled={isLoading}>
                            <FontAwesomeIcon icon={faFilter} />Filters {
                                getActiveFiltersCount()
                                && <span className="filterCount">{getActiveFiltersCount()}</span>
                            }</button>

                    </div>
                </div>

                <div className='flexSpaceBtw textChartSummary'>
                    {
                        isLoading
                            ? [1, 2, 3, 4].map(i => (
                                <TextChartLoader key={i} />
                            ))
                            : (!isMobile && searchFilterData?.regions?.data
                                && searchFilterData?.regions?.data.length > 0)
                            && searchFilterData.summary.data?.filter(i => i.name !== ETHNIC_TEXT)?.map((i, index) => (
                                typeof i.number === "number"
                                && <TextChart
                                    data={i}
                                    key={index}
                                    isLoading={isLoading} />
                            ))
                    }
                </div>
                {
                    (isLoading
                        || (searchFilterData?.regions?.data
                            && searchFilterData?.regions?.data.length > 0)
                    )
                    && <div className='tableActionTab'>
                        {
                            tabsListData?.map(i => (
                                <button
                                    key={i.value}
                                    className={`${selectedTab === i.value ? "unStyledButton active pure-material-button-outlined" : "unStyledButton pure-material-button-outlined"}`}
                                    onClick={() => handleSelectTab(i.value)}>{i.label}</button>
                            ))
                        }
                    </div>
                }
                {
                    (isLoading && !isMobile)
                        ? <div className='tableChartWrapper'>
                            <Loader />
                            <Loader />
                        </div>
                        : mainFilters
                        && <ExploreTab
                            selectedTab={selectedTab}
                            searchFilterData={searchFilterData}
                            isLoading={isLoading}
                        />
                }
                <Drawer
                    open={showDrawer}
                    onClose={handleDrawerOverlayClick}
                    direction='right'
                    size={461}>
                    <MainFilters
                        setIsOpen={setShowDrawer} />
                </Drawer>
            </div>

            {/* HTML FOR SHARE MODAL */}
            <CustomModal
                open={isModalOpen}
                handleClose={toggleModal}
                className={"custom-modal-content"}>
                <div className={"commonModalWrapper"}>
                    <div className="commonModal shareModal">
                        <div className="modalHeader">
                            <h5><FontAwesomeIcon icon={faShareNodes} /> Share the results</h5>
                            <button className="unstyledButton headerClose" onClick={toggleModal}>
                                <FontAwesomeIcon icon={faClose} />
                            </button>
                        </div>
                        <div className="modalBody">
                            <p>Share the report via</p>
                            <div className="shareViaWrap">
                                <div className="socialLinks">
                                    <a onClick={() => setGAnalytics("Facebook")}
                                        href={`https://www.facebook.com/sharer/sharer.php?u=${getSearchUrl()}`}>
                                        <Facebook />
                                        <span className="iconName">Facebook</span>
                                    </a>
                                </div>
                                <div className="socialLinks">
                                    <a onClick={() => setGAnalytics("Twitter")}
                                        href={`https://twitter.com/intent/tweet?url=${getSearchUrl()}&text=`}>
                                        <Twitter />
                                        <span className="iconName">Twitter</span>
                                    </a>
                                </div>
                                <div className="socialLinks">
                                    <a onClick={() => setGAnalytics("Linkedin")} href={`https://www.linkedin.com/shareArticle?mini=true&url=${getSearchUrl()}`}>
                                        <Linkedin />
                                        <span className="iconName">Linkedin</span>
                                    </a>
                                </div>
                                <div className="socialLinks">
                                    <a onClick={() => setGAnalytics("Email")} href={`mailto:?&subject=&cc=&bcc=&body=${getSearchUrl()}`}>
                                        <EnvelopeFill />
                                        <span className="iconName">Email</span>
                                    </a>
                                </div>
                            </div>
                            <p>Or copy the link:</p>
                            <div className="linkCopyWrap">
                                <div className="textField">
                                    <input type="text" value={getSearchUrl()} />
                                </div>
                                <button
                                    className="btn-pink"
                                    onClick={handleCopy}>Copy</button>
                            </div>
                        </div>
                    </div>
                </div>
            </CustomModal>
        </Layout>
    );
}

export default Explore;
